import { notifyResearchAccessChange } from './researchAccessEvents';

/**
 * Grenzen des Ereignisstroms, gespiegelt aus dem Vergleichsparser
 * (MAX_EVENT_BYTES / MAX_STREAM_BYTES in
 * backend/src/services/LocalComparison/stagingLaneBClient.ts). Ein
 * `composition_done` trägt Antwort und ausführliche Schicht in einem Stück;
 * mit der früheren Grenze von 1 MiB bräche der Empfang einer langen Antwort
 * mit RESPONSE_TOO_LARGE ab, obwohl der Lauf fertig ist.
 */
const MAX_STREAM_BYTES = 16 * 1024 * 1024;
const MAX_PENDING_EVENT_BYTES = 4 * 1024 * 1024;
const MAX_ANSWER_CHARACTERS = 512 * 1024;
const MAX_TOOL_NAMES = 128;
const MAX_REVIEW_OBJECTIONS = 256;
const MAX_SOURCE_ITEMS = 2048;
const MAX_METADATA_CHARACTERS = 2 * 1024 * 1024;
const MAX_TOOL_NAME_CHARACTERS = 256;
/**
 * Grenzen der Speicherung, gespiegelt aus
 * backend/src/services/StagingLaneBMetadata.ts: MAX_SUGGESTIONS und
 * MAX_DETAILED_ANSWER_CHARS. Gespeichert wird auf dem Server
 * (services/researchRuns/laneBAnswer.ts); die ausführliche Schicht endet hier
 * an derselben Grenze.
 */
const MAX_SUGGESTIONS = 10;
export const MAX_PERSISTED_DETAILED_ANSWER_CHARACTERS = 400_000;
/** Schranke der Übertragung: ein Vorschlag jenseits davon ist entstellt und fällt weg. */
const MAX_SUGGESTION_CHARACTERS = 400;
/** Hinweise des Abschlusses (etwa eine aus Kostengründen ausgelassene Websuche); jeder erscheint als eigene Meldung. */
const MAX_NOTICES = 10;
const MAX_RESEARCH_ID_CHARACTERS = 128;

/**
 * Der Hinweis, solange das lokale Modell der Bahn C rechnet. Ein Lauf braucht
 * dort fünf bis fünfzehn Minuten; ohne diesen Satz stünde die Oberfläche
 * scheinbar still.
 */
const LOCAL_MODEL_STATUS = 'Lokales Modell antwortet, das dauert einige Minuten …';

/**
 * Variante der Bahn B: 'B' fragt Gemini, 'C' das lokale Modell und 'D' eines
 * der Cortecs-Modelle. Welches das war, nennt die Voreinstellung daneben.
 */
export type StagingLaneVariant = 'B' | 'C' | 'D';

/**
 * Die Wahl des Modells für einen Lauf: die Bahn und, bei D, das Cortecs-Modell.
 * Ein Lauf hält seine Wahl fest; die nächste Frage darf eine andere treffen.
 */
export interface StagingLaneSelection {
  variant: StagingLaneVariant;
  preset?: string;
}

/** Ohne eigene Wahl fragt der Chat Gemini auf der Bahn B. */
export const DEFAULT_STAGING_LANE_SELECTION: StagingLaneSelection = { variant: 'B' };

/** Geschlossene Gestalt eines Voreinstellungsschlüssels der Bahn D. */
const MODEL_PRESET_PATTERN = /^[a-z0-9][a-z0-9._-]{0,63}$/;

export type ReviewStatus = 'certified' | 'objections' | 'unavailable';

export interface ReviewObjection {
  claimText: string;
  kind?: string;
  origin?: string;
  reasoning: string;
}

export interface ReviewAnnotation {
  status: ReviewStatus;
  objections: ReviewObjection[];
  reviewedAt?: string;
}

/** Antwortform, die der Aufrufer angefordert hat. ClaiRIS fordert 'klartext' an. */
export type AnswerProfile = 'kanzlei' | 'klartext';

/**
 * Abschnitt der ausführlichen Schicht, den ein Aufklapp-Chip öffnet. Der
 * Verfasser liefert derzeit B bis F; 'G' nimmt die Speicherung ebenfalls an und
 * wird deshalb hier mitgeführt.
 */
export type RevealSection = 'B' | 'C' | 'D' | 'E' | 'F' | 'G';

/**
 * Vorschlag unter einer Klartext-Antwort: ein Aufklapp-Chip öffnet einen
 * Abschnitt der ausführlichen Schicht, ein Weiterfragen-Chip trägt eine
 * Folgefrage in das Eingabefeld.
 */
export type ResearchSuggestion =
  | { kind: 'reveal'; section: RevealSection; label: string }
  | { kind: 'question'; text: string; origin: 'user_fact' | 'model' };

export interface ResearchMetadata {
  /**
   * 'V25' tragen nur Nachrichten, die aus einem Vergleich fortgesetzt wurden.
   * 'C' ist die Bahn B gegen das lokale Modell: derselbe Weg, anderes Modell.
   */
  lane: 'B' | 'C' | 'D' | 'V25';
  modelLane?: 'gemini' | 'local' | 'cortecs';
  /** Nur die Bahn D trägt das Modell, das geantwortet hat. */
  modelPreset?: string;
  reviewAnnotation?: ReviewAnnotation;
  researchCompletion?: unknown;
  researchRunId?: string;
  /** Zweischicht-Antwort: Antwortform, ausführliche Schicht und Vorschläge. */
  answerProfile?: AnswerProfile;
  /**
   * Die ausführliche Schicht steht auf Abruf bereit, ist aber noch nicht da.
   * Die Release-Linie schickt mit gesetztem Schalter nur den Klartext und
   * meldet die zweite Schicht in den Metadaten des Abschlusses; ohne diesen
   * Vermerk zeigte eine wieder geöffnete Sitzung keinen Aufklapp-Bereich.
   */
  detailAvailable?: boolean;
  detailedAnswer?: string;
  suggestions?: ResearchSuggestion[];
  /** Variante des Vergleichs, aus der diese Nachricht stammt. */
  sourceVariant?: 'A' | 'B';
  comparisonRunId?: string;
}

export interface ResearchStreamSnapshot {
  answer: string;
  statusMessage: string;
  toolNames: string[];
  sources?: unknown;
  verifiedSources?: unknown;
  reviewAnnotation?: ReviewAnnotation;
  researchCompletion?: unknown;
  researchRunId?: string;
  metadata?: Record<string, unknown>;
  structuredData?: unknown;
  jurisprudence?: unknown;
  sourceVerificationStatus?: 'pending' | 'completed' | 'failed';
  risLinkMetadata?: unknown;
  answerProfile?: AnswerProfile;
  /** Der Abschluss meldet eine ausführliche Schicht, die noch abzurufen ist. */
  detailAvailable?: boolean;
  detailedAnswer?: string;
  suggestions?: ResearchSuggestion[];
  /**
   * Die Bahn, die geantwortet hat. Bis die Metadaten da sind, steht hier die
   * angeforderte Variante; danach die Meldung des Servers.
   */
  lane?: StagingLaneVariant;
  /** Das Modell der Bahn D, sobald der Server es meldet. */
  modelPreset?: string;
  /** Der Lauf startete nach einer Unterbrechung neu (run.attempt_reset): ein Hinweis bis zu seinem Ende. */
  restartNotice?: string;
  /** Kennung der gespeicherten Recherche, an der die Bewertung einer Antwort hängt. */
  researchId?: string;
  notices?: string[];
}

/**
 * Die Bahn, unter der eine Antwort dargestellt und gespeichert wird: die
 * Meldung des Servers, solange sie vorliegt, sonst die Bahn B. Eine einzige
 * Stelle, damit das Etikett der laufenden Antwort und die gespeicherte Bahn
 * nicht auseinanderlaufen. Der Wunsch der Anfrage taugt dabei nicht als
 * Nachweis -- welches Modell geantwortet hat, weiß allein der Server.
 */
export function resolvedStagingLane(
  reported: StagingLaneVariant | undefined,
): StagingLaneVariant {
  return reported || 'B';
}

export class ResearchStreamError extends Error {
  readonly code: string;
  readonly partialResult: ResearchStreamSnapshot;

  constructor(code: string, message: string, partialResult: ResearchStreamSnapshot) {
    super(message);
    this.name = 'ResearchStreamError';
    this.code = code;
    this.partialResult = partialResult;
  }
}

function responseTooLarge(state: ResearchStreamSnapshot): ResearchStreamError {
  return new ResearchStreamError(
    'RESPONSE_TOO_LARGE',
    'Die Rechercheantwort ist zu umfangreich und wurde aus Sicherheitsgründen abgebrochen. Bitte grenzen Sie Ihre Frage ein.',
    copySnapshot(state),
  );
}

export interface ParsedSseEvent {
  type: string;
  payload: Record<string, unknown>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Die Anzahl der Einwände in Worten. Eine einzige Stelle für alle drei
 * Abzeichen -- das der ausführlichen Schicht, das der Nachricht und das des
 * Quellen-Fußbereichs --, damit die Einzahl nicht auseinanderläuft.
 */
export function objectionCountLabel(count: number): string {
  return count === 1 ? '1 Einwand' : `${count} Einwände`;
}

/**
 * Deutsches Etikett je Einwandart der Bahn B (ResearchAnswerObjectionKind auf
 * der Serverseite). Ohne sie stand der Drahtwert selbst am Einwand -- der
 * Leser las "wrong_authority" statt des Mangels.
 */
const OBJECTION_KIND_LABELS: Readonly<Record<string, string>> = {
  unsupported: 'Ohne Beleg',
  overreach: 'Über die Belege hinaus',
  missing_condition: 'Einschränkung fehlt',
  wrong_authority: 'Quelle trägt die Aussage nicht',
  not_in_sources: 'Nicht in den recherchierten Quellen',
  unmarked_user_fact: 'Nutzerangabe nicht gekennzeichnet',
  outdated: 'Fassung nicht belegt',
  other: 'Einwand',
};

/**
 * Die festen Begründungen der Laufzeitprüfung (UNKNOWN_ID und
 * UNBOUND_CITATION_REASON in server/src/services/RISearch_research/
 * callerAdapter.ts). Ein älterer Server meldet sie noch als wrong_authority;
 * sie nennen eine Kennung oder Fundstelle, die in keiner gelieferten Quelle
 * steht, wie die neuere Art not_in_sources. Sonst beanstandet der Prüfer
 * unter wrong_authority die Quellenart oder die Übertragbarkeit.
 */
const NOT_IN_SOURCES_REASONS: ReadonlySet<string> = new Set([
  'Kennung nicht in den gelieferten Quellen.',
  'Fundstelle nicht in den gelieferten Quellen.',
]);

/**
 * Das Etikett eines Einwands. Die Abschlussprüfung bleibt ihr eigener
 * Hinweis; sonst benennt die Einwandart den Mangel. Eine unbekannte Art -- ein
 * neuerer Server, eine ältere Oberfläche -- trägt das allgemeine Etikett statt
 * ihres Drahtwerts.
 */
export function reviewObjectionLabel(objection: ReviewObjection): string {
  if (objection.origin === 'completion_review') return 'Prüfhinweis';
  const kind = objection.kind || objection.origin;
  if (kind === 'wrong_authority' && NOT_IN_SOURCES_REASONS.has(objection.reasoning.trim())) {
    return OBJECTION_KIND_LABELS.not_in_sources;
  }
  return (kind ? OBJECTION_KIND_LABELS[kind] : undefined) || 'Einwand';
}

/**
 * Die Quellenart hinter einer Werkzeugkennung der Recherche. Bahn A nennt ihre
 * Werkzeuge ("ris_bundesrecht", "justiz_search"), die Bahn B ihre Korpora
 * ("bundesrecht", "justiz-rs", "justiz-te"); beide führen auf denselben Stamm.
 * Die Bahnen B und C melden die Werkzeuge des Rechercheagenten von JuridicAlly
 * (RISearch_research: "search_decisions", "read_decision", "search_legislation", …).
 * Dieselben Wörter nennt der Server in seinen Statuszeilen (RISearchV25Orchestrator.ts
 * TOOL_DISPLAY_NAMES).
 */
const TOOL_LABELS: ReadonlyMap<string, string> = new Map([
  ['bundesrecht', 'Bundesrecht'],
  ['landesrecht', 'Landesrecht'],
  ['justiz', 'Justiz'],
  ['vfgh', 'VfGH'],
  ['vwgh', 'VwGH'],
  ['bvwg', 'BVwG'],
  ['lvwg', 'Landesverwaltungsgerichte'],
  ['web', 'Websuche'],
  ['web_search_unrestricted', 'Websuche'],
  ['google_search', 'Websuche'],
  ['search_web', 'Websuche'],
  ['read_web_page', 'Websuche'],
  ['search_documents', 'Kollektivverträge'],
  ['search_agb', 'Branchen-AGB'],
  ['search_decisions', 'Entscheidungen'],
  ['read_decision', 'Entscheidungen'],
  ['search_rechtssaetze', 'Rechtssätze'],
  ['search_legislation', 'Rechtsvorschriften'],
]);

/**
 * Die Werkzeuge eines Laufs als deutsche Quellenarten, jede einmal und bei
 * mehreren Aufrufen mit ihrer Zahl ("Bundesrecht (7), Justiz (2)"). Eine
 * unbekannte Kennung steht nie roh da.
 */
export function toolNamesLabel(toolNames: readonly string[]): string {
  const counts = new Map<string, number>();
  for (const name of toolNames) {
    const label = TOOL_LABELS.get(name.replace(/^ris_/, '').replace(/(_search|-rs|-te)$/, '')) ?? 'Weitere Quelle';
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts].map(([label, count]) => (count > 1 ? `${label} (${count})` : label)).join(', ');
}

function asToolNames(value: unknown, state: ResearchStreamSnapshot): string[] {
  if (!Array.isArray(value)) return [];
  if (value.length > MAX_TOOL_NAMES) throw responseTooLarge(state);
  const names = value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
  if (names.some(name => name.length > MAX_TOOL_NAME_CHARACTERS)) throw responseTooLarge(state);
  return names;
}

function ensureCollectionLimit(value: unknown, limit: number, state: ResearchStreamSnapshot): void {
  if (Array.isArray(value) && value.length > limit) throw responseTooLarge(state);
}

function ensureMetadataLimit(value: unknown, state: ResearchStreamSnapshot): void {
  if (value === undefined) return;
  if (JSON.stringify(value).length > MAX_METADATA_CHARACTERS) throw responseTooLarge(state);
}

function setAnswer(state: ResearchStreamSnapshot, answer: string): void {
  if (answer.length > MAX_ANSWER_CHARACTERS) throw responseTooLarge(state);
  state.answer = answer;
}

function appendAnswer(state: ResearchStreamSnapshot, delta: string): void {
  if (state.answer.length + delta.length > MAX_ANSWER_CHARACTERS) throw responseTooLarge(state);
  state.answer += delta;
}

/**
 * Die Bahn, die der Server in den Metadaten meldet. Sie geht der angeforderten
 * Variante vor: welches Modell geantwortet hat, weiß allein der Server -- die
 * Anfrage nennt nur den Wunsch, und eine abgewiesene Bahn C antwortet als B.
 */
function parseStagingLane(value: unknown): StagingLaneVariant | undefined {
  return value === 'B' || value === 'C' || value === 'D' ? value : undefined;
}

/**
 * Das Modell, das den Rechercheweg ausgeführt hat. Beide Bahnen nehmen denselben
 * Weg; allein dieses Feld trennt sie.
 */
export function parseModelLane(value: unknown): StagingLaneVariant | undefined {
  if (value === 'local') return 'C';
  if (value === 'cortecs') return 'D';
  return value === 'gemini' ? 'B' : undefined;
}

/**
 * Das Modell, das die Bahn D ausgeführt hat. Nur ein geschlossener Schlüssel
 * passiert; ein Wortlaut der Gegenseite nie.
 */
export function parseModelPreset(value: unknown): string | undefined {
  return typeof value === 'string' && MODEL_PRESET_PATTERN.test(value) ? value : undefined;
}

/**
 * Die Bahn der fertigen Antwort. Nachgewiesen ist sie allein durch die
 * Modellmeldung des Servers -- in den Metadaten oder im Ereignis selbst.
 * Bleibt sie aus, gilt die Bahn C als unbestätigt und die Antwort trägt gar
 * keine Bahn: die angeforderte Variante ist nur ein Wunsch und beweist nicht,
 * welches Modell geantwortet hat. Die Meldung eines Servers, der noch die Bahn
 * selbst nennt, bleibt für die Bahn B lesbar.
 */
function answeringLane(
  state: ResearchStreamSnapshot,
  payload: Record<string, unknown>,
): StagingLaneVariant | undefined {
  const reported = parseModelLane(state.metadata?.modelLane) || parseModelLane(payload.modelLane);
  if (reported) return reported;
  if (parseStagingLane(state.metadata?.lane) === 'B') return 'B';
  // Eine nicht bestätigte Bahn C oder D bleibt unbestätigt: die angeforderte
  // Variante ist nur ein Wunsch und beweist nichts.
  return state.lane === 'C' || state.lane === 'D' ? undefined : state.lane;
}

/**
 * Geschlossene Gestalt eines Fehlercodes. Ein Wortlaut taugt nicht als Code;
 * ohne diese Schranke stünde beliebiger Text der Gegenseite als Code da.
 */
const ERROR_CODE_PATTERN = /^[A-Z][A-Z0-9_]{0,63}$/;

export function parseErrorCode(value: unknown): string | undefined {
  return typeof value === 'string' && ERROR_CODE_PATTERN.test(value) ? value : undefined;
}

function parseAnswerProfile(value: unknown): AnswerProfile | undefined {
  return value === 'kanzlei' || value === 'klartext' ? value : undefined;
}

function parseDetailedAnswer(value: unknown, state: ResearchStreamSnapshot): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  if (value.length > MAX_ANSWER_CHARACTERS) throw responseTooLarge(state);
  return value;
}

/**
 * Vorschläge des Verfassers. Eine Zeile, die die Form verfehlt, fällt weg und
 * lässt die übrigen stehen; die Liste bleibt auf zehn Einträge begrenzt.
 */
export function parseSuggestions(value: unknown): ResearchSuggestion[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const suggestions = value.flatMap((item): ResearchSuggestion[] => {
    if (!isRecord(item)) return [];
    if (item.kind === 'reveal') {
      const section = item.section;
      const label = asString(item.label);
      const known = section === 'B' || section === 'C' || section === 'D'
        || section === 'E' || section === 'F' || section === 'G';
      if (!known || !label || label.length > MAX_SUGGESTION_CHARACTERS) return [];
      return [{ kind: 'reveal', section, label }];
    }
    if (item.kind === 'question') {
      const text = asString(item.text);
      const origin = item.origin === 'user_fact' || item.origin === 'model' ? item.origin : undefined;
      if (!text || !origin || text.length > MAX_SUGGESTION_CHARACTERS) return [];
      return [{ kind: 'question', text, origin }];
    }
    return [];
  }).slice(0, MAX_SUGGESTIONS);
  return suggestions.length > 0 ? suggestions : undefined;
}

function parseReviewAnnotation(value: unknown, state: ResearchStreamSnapshot): ReviewAnnotation | undefined {
  if (!isRecord(value)) return undefined;
  const status = value.status;
  if (status !== 'certified' && status !== 'objections' && status !== 'unavailable') return undefined;
  ensureCollectionLimit(value.objections, MAX_REVIEW_OBJECTIONS, state);
  const objections = Array.isArray(value.objections)
    ? value.objections.flatMap((item) => {
        if (!isRecord(item)) return [];
        const claimText = asString(item.claimText);
        const kind = asString(item.kind);
        const origin = asString(item.origin);
        const reasoning = asString(item.reasoning);
        return claimText && reasoning && (kind || origin)
          ? [{
              claimText,
              ...(kind ? { kind } : {}),
              ...(origin ? { origin } : {}),
              reasoning,
            }]
          : [];
      })
    : [];
  const reviewedAt = asString(value.reviewedAt);
  return {
    status,
    objections,
    ...(reviewedAt ? { reviewedAt } : {}),
  };
}

export function parseSseBlock(block: string): ParsedSseEvent | null {
  let eventName = '';
  const dataLines: string[] = [];

  for (const line of block.split(/\r\n|\n|\r/)) {
    if (!line || line.startsWith(':')) continue;
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  const data = dataLines.join('\n');
  if (!data || data === '[DONE]') return null;
  try {
    const parsed: unknown = JSON.parse(data);
    if (!isRecord(parsed)) return null;
    const type = eventName || asString(parsed.type);
    return type ? { type, payload: parsed } : null;
  } catch {
    return null;
  }
}

export function nextSseBoundary(buffer: string): { index: number; length: number } | null {
  const match = /\r\n\r\n|\n\n|\r\r/.exec(buffer);
  return match ? { index: match.index, length: match[0].length } : null;
}

export function safeStreamMessage(value: unknown, fallback: string): string {
  const message = asString(value);
  if (!message) return fallback;
  return message.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '').slice(0, 400);
}

/** Fester, wiederholbarer Dienstkonto-Fehler; kein Anlass für einen Browser-Login. */
export function researchErrorMessage(code: string, value: unknown, fallback: string): string {
  if (code === 'STAGING_LANE_B_SESSION_SUPERSEDED') {
    return 'Die Anmeldung des Dienstkontos wurde durch eine neue Anmeldung ersetzt; bitte erneut versuchen.';
  }
  return safeStreamMessage(value, fallback);
}

function copySnapshot(state: ResearchStreamSnapshot): ResearchStreamSnapshot {
  return {
    ...state,
    toolNames: [...state.toolNames],
    reviewAnnotation: state.reviewAnnotation
      ? { ...state.reviewAnnotation, objections: [...state.reviewAnnotation.objections] }
      : undefined,
    suggestions: state.suggestions ? [...state.suggestions] : undefined,
    notices: state.notices ? [...state.notices] : undefined,
  };
}

function parseNotices(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const notices = value
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .slice(0, MAX_NOTICES)
    .map(item => safeStreamMessage(item, ''))
    .filter(Boolean);
  return notices.length > 0 ? notices : undefined;
}

/**
 * Der Auswerter der Ereignisse beider Bahnen, getrennt vom Lesen des Stroms:
 * der Recherchestrom und der Ereignisstrom eines Laufs (researchRunStore.ts)
 * geben ihm dieselben Nutzlasten.
 */
export interface ResearchEventApplier {
  apply(event: ParsedSseEvent | null): void;
  snapshot(): ResearchStreamSnapshot;
  /** Der Fehler, den ein Ereignis `error` gemeldet hat. */
  failure(): { code: string; message: string } | null;
  /** Der Abschluss wie am Ende des Stroms: wirft bei Fehler oder fehlender Antwort. */
  finish(): ResearchStreamSnapshot;
}

export function createResearchEventApplier(
  onUpdate: (snapshot: ResearchStreamSnapshot) => void = () => undefined,
  options: { variant?: StagingLaneVariant } = {},
): ResearchEventApplier {
  const state: ResearchStreamSnapshot = {
    answer: '',
    statusMessage: 'Recherche wird vorbereitet …',
    toolNames: [],
    ...(options.variant ? { lane: options.variant } : {}),
  };
  let terminalDone = false;
  let laneB = false;
  const streamError: { current: { code: string; message: string } | null } = { current: null };

  const emit = () => onUpdate(copySnapshot(state));
  const applyEvent = (event: ParsedSseEvent | null) => {
    if (!event) return;
    const payload = event.payload;

    switch (event.type) {
      case 'status':
        state.statusMessage = safeStreamMessage(payload.message, 'Recherche läuft …');
        emit();
        break;
      case 'tools': {
        state.toolNames = asToolNames(payload.toolNames, state);
        state.statusMessage = state.toolNames.length > 0
          ? `Rechtsquellen werden durchsucht: ${toolNamesLabel(state.toolNames)}`
          : 'Rechtsquellen werden durchsucht …';
        emit();
        break;
      }
      case 'chunk':
        appendAnswer(state, asText(payload.text));
        state.statusMessage = 'Antwort wird erstellt …';
        emit();
        break;
      case 'done': {
        const response = isRecord(payload.response) ? payload.response : payload;
        setAnswer(state, asString(response.answer) || state.answer);
        ensureCollectionLimit(response.sources, MAX_SOURCE_ITEMS, state);
        ensureCollectionLimit(response.verifiedSources, MAX_SOURCE_ITEMS, state);
        ensureMetadataLimit(response.metadata, state);
        state.sources = response.sources;
        state.verifiedSources = response.verifiedSources;
        state.metadata = isRecord(response.metadata) ? response.metadata : undefined;
        state.structuredData = response.structuredData;
        state.jurisprudence = response.jurisprudence;
        state.sourceVerificationStatus = response.sourceVerificationStatus === 'pending'
          || response.sourceVerificationStatus === 'completed'
          || response.sourceVerificationStatus === 'failed'
          ? response.sourceVerificationStatus
          : undefined;
        state.risLinkMetadata = response.risLinkMetadata;
        {
          const researchId = asString(response.researchId);
          state.researchId = researchId && researchId.length <= MAX_RESEARCH_ID_CHARACTERS ? researchId : undefined;
        }
        state.notices = parseNotices(response.notices);
        state.statusMessage = 'Antwort vollständig.';
        terminalDone = true;
        emit();
        break;
      }
      case 'research_progress':
        laneB = true;
        state.toolNames = asToolNames(payload.toolNames, state);
        state.statusMessage = state.toolNames.length > 0
          ? `Rechtsquellen werden durchsucht: ${toolNamesLabel(state.toolNames)}`
          : state.lane === 'C'
            ? LOCAL_MODEL_STATUS
            : 'Rechtsquellen werden durchsucht …';
        emit();
        break;
      case 'research_done':
        laneB = true;
        {
          const toolNames = asToolNames(payload.toolNames, state);
          state.toolNames = toolNames.length > 0 ? toolNames : state.toolNames;
        }
        state.researchRunId = asString(payload.researchRunId) || state.researchRunId;
        if (payload.completion !== undefined) {
          ensureMetadataLimit(payload.completion, state);
          state.researchCompletion = payload.completion;
        }
        state.statusMessage = 'Quellen ausgewertet. Antwort wird erstellt …';
        emit();
        break;
      case 'composition_delta':
        laneB = true;
        appendAnswer(state, asText(payload.text));
        state.statusMessage = terminalDone ? 'Quellenprüfung läuft …' : 'Antwort wird erstellt …';
        emit();
        break;
      case 'composition_restart':
        laneB = true;
        setAnswer(state, '');
        terminalDone = false;
        state.statusMessage = 'Antwort wird neu erstellt …';
        emit();
        break;
      case 'composition_done': {
        laneB = true;
        setAnswer(state, asString(payload.answer) || state.answer);
        ensureCollectionLimit(payload.sources, MAX_SOURCE_ITEMS, state);
        ensureCollectionLimit(payload.verifiedSources, MAX_SOURCE_ITEMS, state);
        ensureMetadataLimit(payload.metadata, state);
        state.sources = payload.sources;
        state.verifiedSources = payload.verifiedSources;
        state.metadata = isRecord(payload.metadata) ? payload.metadata : undefined;
        state.researchRunId = asString(state.metadata?.researchRunId) || state.researchRunId;
        state.lane = answeringLane(state, payload);
        state.modelPreset = state.lane === 'D'
          ? parseModelPreset(state.metadata?.modelPreset) || parseModelPreset(payload.modelPreset)
          : undefined;
        state.answerProfile = parseAnswerProfile(payload.answerProfile);
        // Der Vermerk steht in den Metadaten des Abschlusses. Nur ein
        // ausdrückliches `true` gilt; alles andere heißt: keine zweite Schicht.
        state.detailAvailable = state.metadata?.detailAvailable === true ? true : undefined;
        state.detailedAnswer = parseDetailedAnswer(payload.detailedAnswer, state);
        state.suggestions = parseSuggestions(payload.suggestions);
        state.statusMessage = 'Antwort vollständig. Quellenprüfung läuft …';
        terminalDone = true;
        emit();
        break;
      }
      case 'review': {
        laneB = true;
        state.reviewAnnotation = parseReviewAnnotation(payload.reviewAnnotation, state);
        if (payload.researchCompletion !== undefined) {
          ensureMetadataLimit(payload.researchCompletion, state);
          state.researchCompletion = payload.researchCompletion;
        }
        if (payload.verifiedSources !== undefined) {
          ensureCollectionLimit(payload.verifiedSources, MAX_SOURCE_ITEMS, state);
          state.verifiedSources = payload.verifiedSources;
        }
        state.statusMessage = state.reviewAnnotation?.status === 'certified'
          ? 'Quellenprüfung abgeschlossen.'
          : state.reviewAnnotation?.status === 'objections'
            ? 'Quellenprüfung mit Einwänden abgeschlossen.'
            : 'Quellenprüfung derzeit nicht verfügbar.';
        emit();
        break;
      }
      case 'error': {
        const code = parseErrorCode(payload.code) || 'RESEARCH_FAILED';
        streamError.current = {
          code,
          message: researchErrorMessage(code, payload.error ?? payload.message, 'Die Recherche ist fehlgeschlagen. Bitte versuchen Sie es erneut.'),
        };
        state.statusMessage = streamError.current.message;
        emit();
        break;
      }
      default:
        break;
    }
  };

  return {
    apply: applyEvent,
    snapshot: () => copySnapshot(state),
    failure: () => streamError.current,
    finish: () => {
      if (streamError.current) {
        throw new ResearchStreamError(streamError.current.code, streamError.current.message, copySnapshot(state));
      }
      if (!terminalDone) {
        throw new ResearchStreamError(
          'STREAM_INTERRUPTED',
          'Die Rechercheverbindung wurde beendet, bevor eine vollständige Antwort vorlag. Bitte versuchen Sie es erneut.',
          copySnapshot(state),
        );
      }
      if (laneB && !state.reviewAnnotation) {
        state.reviewAnnotation = { status: 'unavailable', objections: [] };
        state.statusMessage = 'Antwort vollständig. Quellenprüfung derzeit nicht verfügbar.';
        emit();
      }
      return copySnapshot(state);
    },
  };
}

/**
 * Liest einen SSE-Strom blockweise, mit den Grenzen für Strom und Einzelereignis.
 * Liefert `onBlock` true, endet das Lesen vorzeitig.
 */
export async function readSseBlocks(
  stream: ReadableStream<Uint8Array>,
  onBlock: (block: string) => boolean | void,
  tooLarge: () => ResearchStreamError,
): Promise<void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let totalBytes = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (value) {
        totalBytes += value.byteLength;
        if (totalBytes > MAX_STREAM_BYTES) throw tooLarge();
        buffer += decoder.decode(value, { stream: true });
      }

      let boundary = nextSseBoundary(buffer);
      while (boundary) {
        const block = buffer.slice(0, boundary.index);
        if (new TextEncoder().encode(block).byteLength > MAX_PENDING_EVENT_BYTES) {
          throw tooLarge();
        }
        buffer = buffer.slice(boundary.index + boundary.length);
        if (onBlock(block) === true) {
          try { await reader.cancel(); } catch { /* reader may already be closed */ }
          return;
        }
        boundary = nextSseBoundary(buffer);
      }

      if (new TextEncoder().encode(buffer).byteLength > MAX_PENDING_EVENT_BYTES) {
        throw tooLarge();
      }

      if (done) break;
    }
    buffer += decoder.decode();
    if (buffer.trim()) onBlock(buffer);
  } catch (error) {
    if (error instanceof ResearchStreamError) {
      try { await reader.cancel(); } catch { /* reader may already be closed */ }
    }
    throw error;
  }
}

/**
 * Die Abweisung einer Anfrage als ResearchStreamError. Der Grund des Servers
 * steht vor den Kopfzeilen im Rumpf. Nennt er einen eigenen Code, geht dieser
 * weiter -- sonst bliebe von einer abgewiesenen Bahn C nur der nackte
 * Statuscode übrig.
 */
export async function researchResponseError(
  response: Response,
  genericMessage = `Die Recherche konnte nicht gestartet werden (${response.status}).`,
): Promise<ResearchStreamError> {
  let message = genericMessage;
  let code = `HTTP_${response.status}`;
  try {
    const payload: unknown = await response.json();
    if (isRecord(payload)) {
      code = parseErrorCode(payload.code) || code;
      message = researchErrorMessage(code, payload.error, message);
    }
  } catch { /* an error response may have no JSON body */ }
  // Abweisungen wegen Zugang, Grenze oder Kosten (403/409/429) frischen den Zugangsstand auf, wie bei apiClient.
  notifyResearchAccessChange(response.status, code);
  // Ein Serverfehler ohne eigenen Code trägt einen internen Text; angezeigt wird nur der allgemeine Hinweis.
  if (response.status >= 500 && code === `HTTP_${response.status}`) message = genericMessage;
  return new ResearchStreamError(
    code,
    message,
    { answer: '', statusMessage: message, toolNames: [] },
  );
}
